import { useState, useEffect, type FormEvent } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Loader2,
  Building2,
  Sparkles,
  UserCheck,
  Mail,
  Lock,
  User,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/store/authStore";
import { getErrorMessage } from "@/services/apiClient";
import {
  getGoogleAuthUrl,
  verifyGoogleCredential,
  verifyInvitation,
} from "@/services/authService";

const INDUSTRIES = [
  { id: "general", label: "General Business / Agency" },
  { id: "technology", label: "Technology & Software / SaaS" },
  { id: "ecommerce", label: "E-Commerce & Retail" },
  { id: "logistics", label: "Logistics & Supply Chain" },
  { id: "finance", label: "Finance & Professional Services" },
  { id: "healthcare", label: "Healthcare & Life Sciences" },
];

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const signup = useAuthStore((s) => s.signup);
  const completeOnboarding = useAuthStore((s) => s.completeOnboarding);
  const acceptInvite = useAuthStore((s) => s.acceptInvite);
  const setSessionFromTokens = useAuthStore((s) => s.setSessionFromTokens);
  const loading = useAuthStore((s) => s.loading);

  // Modes: "signin" | "signup" | "onboarding" | "invite"
  const [mode, setMode] = useState<"signin" | "signup" | "onboarding" | "invite">("signin");

  // Sign In Form
  const [loginEmail, setLoginEmail] = useState("karan@acme-logistics.com");
  const [loginPassword, setLoginPassword] = useState("Demo@123");

  // Manual Signup Form
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [industryId, setIndustryId] = useState("general");

  // Google Onboarding Form
  const [onboardingToken, setOnboardingToken] = useState("");
  const [googleUserEmail, setGoogleUserEmail] = useState("");
  const [googleUserName, setGoogleUserName] = useState("");
  const [googleAvatar, setGoogleAvatar] = useState("");
  const [googleCompanyName, setGoogleCompanyName] = useState("");
  const [googleIndustryId, setGoogleIndustryId] = useState("general");

  // Invitation Form
  const [inviteToken, setInviteToken] = useState("");
  const [inviteOrgName, setInviteOrgName] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [invitePassword, setInvitePassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);

  // Check URL params on mount
  useEffect(() => {
    // 1. Direct Token redirect from Google callback
    const token = searchParams.get("token");
    const refresh = searchParams.get("refresh");
    if (token) {
      setSessionFromTokens({
        access_token: token,
        refresh_token: refresh || undefined,
      }).catch((err) => {
        setError(getErrorMessage(err, "Failed to restore session"));
      });
      return;
    }

    // 2. Google First-Time Onboarding
    const isOnboarding = searchParams.get("onboarding") === "true";
    const obToken = searchParams.get("onboarding_token");
    if (isOnboarding && obToken) {
      setOnboardingToken(obToken);
      setGoogleUserEmail(searchParams.get("email") || "");
      setGoogleUserName(searchParams.get("name") || "");
      setGoogleAvatar(searchParams.get("picture") || "");
      setMode("onboarding");
      return;
    }

    // 3. Invitation link
    const invTok = searchParams.get("invite");
    if (invTok) {
      setInviteToken(invTok);
      setMode("invite");
      verifyInvitation(invTok)
        .then((res) => {
          setInviteOrgName(res.organization_name);
          setInviteRole(res.role);
          setInviteEmail(res.email);
          setInviteName(res.email.split("@")[0].replace(".", " ").replace(/\b\w/g, (c) => c.toUpperCase()));
        })
        .catch(() => {
          setError("Invitation link is invalid or expired.");
        });
      return;
    }

    // Check for error in query string
    const queryError = searchParams.get("error");
    if (queryError) {
      setError(queryError);
    }
  }, [searchParams, setSessionFromTokens]);

  if (user) {
    const redirectTo = (location.state as { from?: string } | null)?.from || "/";
    return <Navigate to={redirectTo} replace />;
  }

  // Handle Standard Login
  async function onLoginSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await login(loginEmail, loginPassword);
    } catch (err) {
      setError(getErrorMessage(err, "Invalid email or password"));
    }
  }

  // Handle Manual Signup
  async function onSignupSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!companyName.trim()) {
      setError("Please provide your Company / Organization Name.");
      return;
    }
    if (signupPassword.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    try {
      await signup({
        name: signupName.trim(),
        email: signupEmail.trim(),
        password: signupPassword,
        company_name: companyName.trim(),
        industry_id: industryId,
      });
    } catch (err) {
      setError(getErrorMessage(err, "Signup failed. Please try again."));
    }
  }

  // Handle Google Onboarding Submission
  async function onGoogleOnboardingSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!googleCompanyName.trim()) {
      setError("Please enter your Company / Organization Name.");
      return;
    }
    try {
      await completeOnboarding({
        onboarding_token: onboardingToken,
        company_name: googleCompanyName.trim(),
        industry_id: googleIndustryId,
      });
    } catch (err) {
      setError(getErrorMessage(err, "Onboarding failed. Please try again."));
    }
  }

  // Handle Invitation Acceptance via Password
  async function onAcceptInviteSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!invitePassword || invitePassword.length < 6) {
      setError("Please enter a password of at least 6 characters.");
      return;
    }
    try {
      await acceptInvite({
        token: inviteToken,
        name: inviteName.trim(),
        password: invitePassword,
      });
    } catch (err) {
      setError(getErrorMessage(err, "Failed to accept invitation."));
    }
  }

  // Handle Continue with Google
  async function onContinueWithGoogle() {
    setError("");
    setGoogleLoading(true);
    try {
      const { authorize_url } = await getGoogleAuthUrl(
        window.location.origin,
        inviteToken || undefined
      );
      window.location.href = authorize_url;
    } catch (err) {
      setGoogleLoading(false);
      setError(getErrorMessage(err, "Unable to launch Google authentication."));
    }
  }

  // Dev / Demo Google Simulation (Allows instant testing without live Google API keys)
  async function onSimulateGoogleSignIn(simulateEmail: string, simulateName: string) {
    setError("");
    setGoogleLoading(true);
    try {
      const res = await verifyGoogleCredential({
        simulate_email: simulateEmail,
        simulate_name: simulateName,
        invitation_token: inviteToken || undefined,
      });
      if (res.is_existing && res.token) {
        await setSessionFromTokens(res.token);
      } else if (res.onboarding_token) {
        setOnboardingToken(res.onboarding_token);
        setGoogleUserEmail(res.email || simulateEmail);
        setGoogleUserName(res.name || simulateName);
        setMode("onboarding");
      }
    } catch (err) {
      setError(getErrorMessage(err, "Google sign-in simulation failed."));
    } finally {
      setGoogleLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-50 px-4 py-12 dark:bg-zinc-950">
      {/* Decorative Brand Glows */}
      <div className="pointer-events-none absolute -right-32 -bottom-32 h-[520px] w-[520px] rounded-full bg-[var(--amos-brand)] opacity-20 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 -top-24 h-[380px] w-[380px] rounded-full bg-[var(--amos-brand)] opacity-15 blur-3xl" />

      <div className="relative w-full max-w-lg rounded-2xl border border-zinc-200/80 bg-white/95 p-8 shadow-xl backdrop-blur-md dark:border-zinc-800 dark:bg-zinc-900/90">
        
        {/* Brand Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-10 w-auto rounded-xl shadow-sm" />
            <div>
              <h1 className="text-xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
                MarketIQ
              </h1>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Marketing Intelligence OS
              </p>
            </div>
          </div>
          {mode !== "onboarding" && mode !== "invite" && (
            <div className="flex rounded-lg bg-zinc-100 p-1 dark:bg-zinc-800">
              <button
                type="button"
                onClick={() => { setMode("signin"); setError(""); }}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
                  mode === "signin"
                    ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-950 dark:text-zinc-100"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => { setMode("signup"); setError(""); }}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
                  mode === "signup"
                    ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-950 dark:text-zinc-100"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
                }`}
              >
                Create Account
              </button>
            </div>
          )}
        </div>

        {/* Global Error Banner */}
        {error && (
          <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-xs font-medium text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        {/* ================================================================= */}
        {/* MODE 1: SIGN IN (Existing Users)                                  */}
        {/* ================================================================= */}
        {mode === "signin" && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                Welcome Back
              </h2>
              <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                Sign in to your organization workspace.
              </p>
            </div>

            {/* Google Single Sign-On Button */}
            <button
              type="button"
              onClick={onContinueWithGoogle}
              disabled={googleLoading || loading}
              className="flex w-full items-center justify-center gap-3 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-700 shadow-sm transition hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700/80"
            >
              {googleLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                  />
                </svg>
              )}
              Continue with Google
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => onSimulateGoogleSignIn("demo.google@company.com", "Demo Google User")}
                className="text-[11px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 underline"
              >
                Instant Demo Google Sign-In
              </button>
            </div>

            <div className="relative my-3 flex items-center justify-center">
              <div className="w-full border-t border-zinc-200 dark:border-zinc-800" />
              <span className="bg-white px-2 text-[11px] uppercase tracking-wider text-zinc-400 dark:bg-zinc-900">
                Or with email
              </span>
            </div>

            <form onSubmit={onLoginSubmit} className="space-y-3.5">
              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
                  <input
                    type="email"
                    required
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="w-full rounded-lg border border-zinc-200 bg-zinc-50/50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[var(--amos-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-100"
                  />
                </div>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-xs font-medium text-zinc-700 dark:text-zinc-300">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    data-testid="login-forgot-password"
                    className="text-xs font-medium text-[var(--amos-brand)] transition hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full rounded-lg border border-zinc-200 bg-zinc-50/50 py-2.5 pl-9 pr-10 text-sm outline-none transition focus:border-[var(--amos-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Sign In
              </Button>
            </form>

            {/* Demo Accounts Pill List */}
            <div className="mt-4 rounded-xl border border-sky-200/80 bg-sky-50/60 p-3 text-xs text-sky-950 dark:border-sky-900/50 dark:bg-sky-950/20 dark:text-sky-300">
              <div className="font-semibold text-sky-800 dark:text-sky-200">
                Quick Demo Accounts:
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => { setLoginEmail("karan@acme-logistics.com"); setLoginPassword("Demo@123"); }}
                  className="rounded-md bg-sky-100/80 px-2 py-1 font-mono text-[11px] text-sky-900 hover:bg-sky-200 dark:bg-sky-900/40 dark:text-sky-200"
                >
                  karan@acme-logistics.com (Admin)
                </button>
                <button
                  type="button"
                  onClick={() => { setLoginEmail("admin@marketiq.io"); setLoginPassword("Admin@123"); }}
                  className="rounded-md bg-sky-100/80 px-2 py-1 font-mono text-[11px] text-sky-900 hover:bg-sky-200 dark:bg-sky-900/40 dark:text-sky-200"
                >
                  admin@marketiq.io
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* MODE 2: MANUAL SIGNUP (1. Create new Organization + Admin user)     */}
        {/* ================================================================= */}
        {mode === "signup" && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                Create Organization Workspace
              </h2>
              <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                Each organization gets a dedicated, isolated workspace.
              </p>
            </div>

            {/* Google Signup Button */}
            <button
              type="button"
              onClick={onContinueWithGoogle}
              disabled={googleLoading || loading}
              className="flex w-full items-center justify-center gap-3 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-700 shadow-sm transition hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700/80"
            >
              {googleLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                  />
                </svg>
              )}
              Sign Up with Google
            </button>

            <div className="relative my-4 flex items-center justify-center">
              <div className="w-full border-t border-zinc-200 dark:border-zinc-800" />
              <span className="bg-white px-2 text-[11px] uppercase tracking-wider text-zinc-400 dark:bg-zinc-900">
                Or with details
              </span>
            </div>

            <form onSubmit={onSignupSubmit} className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
                  <input
                    type="text"
                    required
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    placeholder="e.g. Sarah Connor"
                    className="w-full rounded-lg border border-zinc-200 bg-zinc-50/50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[var(--amos-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-100"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Work Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
                  <input
                    type="email"
                    required
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="w-full rounded-lg border border-zinc-200 bg-zinc-50/50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[var(--amos-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-100"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Password (min. 6 characters)
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    placeholder="Create a strong password"
                    className="w-full rounded-lg border border-zinc-200 bg-zinc-50/50 py-2.5 pl-9 pr-10 text-sm outline-none transition focus:border-[var(--amos-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Explicit Company Name Prompt */}
              <div className="rounded-xl border border-amber-200/90 bg-amber-50/40 p-3.5 dark:border-amber-900/40 dark:bg-amber-950/20">
                <label className="mb-1 flex items-center gap-1.5 text-xs font-bold text-amber-900 dark:text-amber-200">
                  <Building2 className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                  Company / Organization Name (Required)
                </label>
                <p className="mb-2 text-[11px] text-amber-800/80 dark:text-amber-300/80">
                  Your team, campaigns, and settings will be scoped to this organization.
                </p>
                <input
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Innovations Inc."
                  className="w-full rounded-lg border border-amber-300/80 bg-white px-3 py-2 text-sm font-medium outline-none transition focus:border-[var(--amos-brand)] focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-amber-700/60 dark:bg-zinc-900 dark:text-zinc-100"
                />

                <div className="mt-3">
                  <label className="mb-1 block text-[11px] font-semibold text-zinc-600 dark:text-zinc-400">
                    Industry Sector
                  </label>
                  <select
                    value={industryId}
                    onChange={(e) => setIndustryId(e.target.value)}
                    className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-medium outline-none transition dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200"
                  >
                    {INDUSTRIES.map((ind) => (
                      <option key={ind.id} value={ind.id}>
                        {ind.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Create Organization & Start
              </Button>
            </form>
          </div>
        )}

        {/* ================================================================= */}
        {/* MODE 3: GOOGLE FIRST-TIME ONBOARDING (Ask Company Name)           */}
        {/* ================================================================= */}
        {mode === "onboarding" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-3.5 dark:border-emerald-900/50 dark:bg-emerald-950/30">
              {googleAvatar ? (
                <img
                  src={googleAvatar}
                  alt={googleUserName}
                  className="h-10 w-10 rounded-full border border-emerald-300 shadow-sm"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-200 text-emerald-800 font-bold">
                  {googleUserName.charAt(0) || "U"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-900 dark:text-emerald-200">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  Google Verified Identity
                </div>
                <div className="truncate text-xs font-medium text-emerald-800 dark:text-emerald-300">
                  {googleUserName} ({googleUserEmail})
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
                Organization Onboarding
              </h2>
              <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                Welcome to MarketIQ! Please name your organization to create your dedicated workspace. You will be assigned the <strong>Admin</strong> role.
              </p>
            </div>

            <form onSubmit={onGoogleOnboardingSubmit} className="space-y-4">
              <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-4 dark:border-zinc-800 dark:bg-zinc-800/40">
                <label className="mb-1 flex items-center gap-1.5 text-xs font-bold text-zinc-800 dark:text-zinc-200">
                  <Building2 className="h-4 w-4 text-[var(--amos-brand)]" />
                  Company / Organization Name (Required)
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={googleCompanyName}
                  onChange={(e) => setGoogleCompanyName(e.target.value)}
                  placeholder="e.g. Stark Industries"
                  className="w-full rounded-lg border border-zinc-300 bg-white px-3.5 py-2.5 text-sm font-medium outline-none transition focus:border-[var(--amos-brand)] focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                />

                <div className="mt-3.5">
                  <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">
                    Primary Industry Sector
                  </label>
                  <select
                    value={googleIndustryId}
                    onChange={(e) => setGoogleIndustryId(e.target.value)}
                    className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-medium outline-none transition dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200"
                  >
                    {INDUSTRIES.map((ind) => (
                      <option key={ind.id} value={ind.id}>
                        {ind.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                <span>Isolated tenant with dedicated encryption & permissions.</span>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
                Complete Setup & Launch Workspace
              </Button>
            </form>
          </div>
        )}

        {/* ================================================================= */}
        {/* MODE 4: ACCEPT INVITATION (Join existing Organization)             */}
        {/* ================================================================= */}
        {mode === "invite" && (
          <div className="space-y-4">
            <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-4 dark:border-sky-900/50 dark:bg-sky-950/30">
              <div className="flex items-center gap-2 font-bold text-sky-900 dark:text-sky-200">
                <UserCheck className="h-4 w-4 text-sky-600" />
                Team Invitation
              </div>
              <p className="mt-1 text-xs text-sky-800 dark:text-sky-300">
                You've been invited to join <strong>{inviteOrgName || "an Organization"}</strong> as a{" "}
                <span className="rounded bg-sky-200/80 px-1.5 py-0.5 font-semibold uppercase text-sky-900 dark:bg-sky-900/60 dark:text-sky-200">
                  {inviteRole}
                </span>
                .
              </p>
              <div className="mt-2 text-[11px] text-sky-700/80 dark:text-sky-400">
                Invited email: <strong>{inviteEmail}</strong>
              </div>
            </div>

            {/* Google Fast Join */}
            <button
              type="button"
              onClick={onContinueWithGoogle}
              disabled={googleLoading || loading}
              className="flex w-full items-center justify-center gap-3 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-700 shadow-sm transition hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                />
              </svg>
              Join with Google
            </button>

            <div className="relative my-4 flex items-center justify-center">
              <div className="w-full border-t border-zinc-200 dark:border-zinc-800" />
              <span className="bg-white px-2 text-[11px] uppercase tracking-wider text-zinc-400 dark:bg-zinc-900">
                Or set password
              </span>
            </div>

            <form onSubmit={onAcceptInviteSubmit} className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Your Name
                </label>
                <input
                  type="text"
                  required
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="w-full rounded-lg border border-zinc-200 bg-zinc-50/50 py-2.5 px-3 text-sm outline-none transition focus:border-[var(--amos-brand)] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Create Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={invitePassword}
                  onChange={(e) => setInvitePassword(e.target.value)}
                  placeholder="Set your account password"
                  className="w-full rounded-lg border border-zinc-200 bg-zinc-50/50 py-2.5 px-3 text-sm outline-none transition focus:border-[var(--amos-brand)] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Accept Invitation & Join Team
              </Button>
            </form>
          </div>
        )}

        <div className="mt-8 flex items-center justify-center gap-4 text-xs text-zinc-400 dark:text-zinc-500">
          <Link to="/privacy" className="transition hover:text-zinc-600 dark:hover:text-zinc-300">
            Privacy Policy
          </Link>
          <span>·</span>
          <Link to="/privacy#data-deletion" className="transition hover:text-zinc-600 dark:hover:text-zinc-300">
            Data Deletion Instructions
          </Link>
        </div>
      </div>
    </div>
  );
}
